<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import { DataTable, type DataTableContextMenuProvider } from '@hantera/design-system'
import {
  type EventData,
  type GraphDataProviderColumn,
  type GraphDataProviderQueryRoot,
  type GraphDataProviderState,
  useContext,
  useDialogs,
  useEvents,
  useGraphDataProvider,
  useLocalization,
  useProcesses,
} from '@hantera/portal-app'
import type { JobDefinition } from '@/backend/jobs.types'
import type { useJobDefinitionDetailsStore } from '@/stores/jobs/job-definition-details-store'
import JobDialog from './JobDialog.vue'
import ScheduleJobDialog from './ScheduleJobDialog.vue'
import BatchScheduleDialog from './BatchScheduleDialog.vue'
import useBackend from '@/composition/useBackend'

const props = defineProps<{
  jobDefinitionId: string
}>()

const t = useLocalization('jobs')
const dialogs = useDialogs()
const context = useContext()
const procs = useProcesses()
const { jobs: jobsService } = useBackend()
const store = inject<ReturnType<typeof useJobDefinitionDetailsStore>>('jobDefinitionStore')!

const jobDefinition = ref<JobDefinition | null>(null)

onMounted(async () => {
  try {
    jobDefinition.value = await jobsService.getJobDefinition(props.jobDefinitionId)
  } catch {
    // Parameter metadata is optional — dialog still works without it
  }
})

const state = ref<GraphDataProviderState>({
  filters: {},
})

const columns = ref<GraphDataProviderColumn[]>([
  { field: 'jobState', queryPath: ['jobs'] },
  { field: 'scheduledAt', queryPath: ['jobs'] },
  { field: 'startedAt', queryPath: ['jobs'] },
  { field: 'finishedAt', queryPath: ['jobs'] },
  { field: 'elapsedMs', queryPath: ['jobs'] },
  { field: 'source', queryPath: ['jobs'] },
])

const query = computed<GraphDataProviderQueryRoot>(() => ({
  edge: 'jobs',
  filter: `jobDefinitionId == '${props.jobDefinitionId.replace(`'`, `''`)}'`,
  dataFields: ['jobId', 'jobState', 'scheduledAt', 'startedAt', 'finishedAt', 'elapsedMs', 'source'],
  orderBy: 'scheduledAt desc',
  availableFields: [
    { field: 'jobId', label: t('detail.jobs.columns.jobId') },
    { field: 'jobState', label: t('detail.jobs.columns.state') },
    { field: 'scheduledAt', label: t('detail.jobs.columns.scheduledAt') },
    { field: 'startedAt', label: t('detail.jobs.columns.startedAt') },
    { field: 'finishedAt', label: t('detail.jobs.columns.finishedAt') },
    { field: 'elapsedMs', label: t('detail.jobs.columns.elapsedMs') },
    { field: 'source', label: t('detail.jobs.columns.source') },
  ],
}))

const provider = useGraphDataProvider(query, columns, state, {
  rowHue(row: Record<string, unknown>): string | number | undefined {
    const jobState = row['jobs.jobState']
    switch (jobState) {
      case 'completed':
        return 142
      case 'failed':
        return 0
      case 'running':
        return 200
      case 'cancelled':
        return 280
      default:
        return undefined
    }
  },
})

const events = useEvents()

function handleJobEvent(event: EventData) {
  if (event.eventType === 'jobScheduled') {
    provider.refresh()
    return
  }

  if (event.eventType === 'jobStarted') {
    const data = event.data as { jobId: string; startedAt: string }
    provider.updateRow(data.jobId, {
      'jobs.jobState': 'Running',
      'jobs.startedAt': data.startedAt,
    })
    return
  }

  if (event.eventType === 'jobCompleted') {
    const data = event.data as { jobId: string; completedAt: string; duration: number }
    provider.updateRow(data.jobId, {
      'jobs.jobState': 'Completed',
      'jobs.finishedAt': data.completedAt,
      'jobs.elapsedMs': data.duration,
    })
    return
  }

  if (event.eventType === 'jobFailed') {
    const data = event.data as { jobId: string; failedAt: string }
    provider.updateRow(data.jobId, {
      'jobs.jobState': 'Failed',
      'jobs.finishedAt': data.failedAt,
    })
  }
}

if (events.available) {
  events.subscribe({
    path: `jobs/${props.jobDefinitionId}`,
    events: ['jobScheduled', 'jobStarted', 'jobCompleted', 'jobFailed'],
    onEvent: handleJobEvent,
  })
}

const contextMenuProvider: DataTableContextMenuProvider = {
  forHeader: () => [[
    { label: t('detail.jobs.contextMenu.refresh'), icon: 'reload', action: () => provider.refresh() }
  ]],
  forRow: () => [[
    { label: t('detail.jobs.contextMenu.refresh'), icon: 'reload', action: () => provider.refresh() }
  ]]
}

const selectedKeys = ref<Set<string>>(new Set())

const selectedKey = computed(() => {
  if (selectedKeys.value.size === 0) return null
  return Array.from(selectedKeys.value)[0]
})

const canCancel = computed(() => {
  if (!selectedKey.value) return false
  const row = provider.rows.value.find((r: { key: string }) => r.key === selectedKey.value)
  if (!row) return false
  const record = provider.toRecord(row.values)
  const jobState = record['jobs.jobState']
  return jobState === 'pending'
})

function handleDefaultAction() {
  if (selectedKey.value) {
    store.selectJob(selectedKey.value)
    openJobDialog(selectedKey.value)
  }
}

async function openJobDialog(jobId: string) {
  if (!jobDefinition.value) return
  const job = await store.getJob(jobId)
  const controller = dialogs.openModal(
    t('detail.dialog.title'),
    JobDialog,
    false,
    {
      job,
      jobDefinition: jobDefinition.value,
      parameterMetadata: jobDefinition.value.parameters ?? null,
      onReschedule: async (parameters: Record<string, unknown>) => {
        await store.scheduleJob(parameters)
        controller.close()
        provider.refresh()
      },
      onClose: () => controller.close(),
      parentContext: context
    }
  )
}

function openScheduleDialog() {
  const controller = dialogs.openModal(
    t('detail.scheduleDialog.title'),
    ScheduleJobDialog,
    false,
    {
      jobDefinitionId: props.jobDefinitionId,
      onSchedule: async (parameters: Record<string, unknown>) => {
        await store.scheduleJob(parameters)
        controller.close()
        provider.refresh()
      },
      onCancel: () => controller.close(),
    }
  )
}

function openBatchScheduleDialog() {
  if (!jobDefinition.value) return
  const controller = dialogs.openModal(
    t('detail.batchScheduleDialog.title'),
    BatchScheduleDialog,
    false,
    {
      jobDefinition: jobDefinition.value,
      onClose: () => {
        controller.close()
        provider.refresh()
      },
    }
  )
}

async function cancelSelectedJob() {
  if (!selectedKey.value) return

  const confirmed = await dialogs.confirm(
    t('detail.jobs.cancelConfirm.message'),
    t('detail.jobs.cancelConfirm.title'),
    undefined,
    undefined,
    true
  )

  if (confirmed) {
    try {
      await store.cancelJob(selectedKey.value)
    } catch (error: any) {
      procs.error(error)
    }
    provider.refresh()
    selectedKeys.value = new Set()
  }
}

defineExpose({
  openScheduleDialog,
  openBatchScheduleDialog,
  cancelSelectedJob,
  canCancel,
  refresh: () => provider.refresh(),
})
</script>

<template>
  <DataTable
    :provider="provider"
    :contextMenuProvider="contextMenuProvider"
    cache-key="job-definition-jobs"
    v-model:selected="selectedKeys"
    @defaultAction="handleDefaultAction"
  />
</template>

<style scoped>
div {
  height: 100%;
}
</style>
